import { listAdoptedRuleMembers } from '../training/training-rule-members'
import { setWorkspaceSessionUser } from './session-user-db-task'
import { pageSourceColumns } from './workspace-tables'
import { removedQuestionParts } from './page-read'
import { searchKey } from '../search/search-key'
import { cardReadColumns, cardVisibleTo, learnerCards, readablePagesOf } from './question-card-triggers'
import { collectMediaSources } from './media-sources'
import { commitPageEdit } from './commit-page-edit'
import { pageLifecycleStatements } from './page-relations-derivation'
import type { Tel } from '@w/tel'
import { s } from '@w/schema'
import { collectQuestionParts } from './question-parts'
import { ulid } from '@w/utils'
import type { WorkspaceDb } from './workspace-tables'
import type { LearnerAction, ReviewEvent } from './card-history'
import { reviewEventSchema } from './card-history'
import { newPageDocument, readPageDocument, pageDocumentInput } from './page-document'
import { rulePracticePair } from '../lexicon/rule-blocks'
import { mintQuestionBlocks, hasQid } from '../lexicon/entry-blocks'
import type { Lexeme, RuleBlock } from '../lexicon/lexicon-model'
import { findBlock } from '@w/content-blocks/src/traverse'
import { nullTel } from '@w/tel'
import { crdt } from '@w/crdt'
import { blockId, type Blocks } from '@w/content-blocks'

// Module scope (Zod: define schemas once): inline schemas paid construction on every call.
const nodeSchema = s.object({ node: s.string() })

/** Technical principal is set before writes/applies; opening never projects pages. */
export function completeLearnerBootstrap(db: WorkspaceDb, userId: string) {
  return setWorkspaceSessionUser(db.driver, userId)
}

export function actOnCard(db: WorkspaceDb, input: { userId: string; deviceId: string; qid: string; action: LearnerAction; now?: Date; eventId?: string; translationLanguage?: string; practiceRep?: number }) {
  return Promise.resolve(db.transaction(async tx => {
    const card = await readLearnerCard(tx,input.userId,input.qid)
    const action = input.action
    const now = input.now ?? new Date()
    if (action.kind === 'resolved' ? card.deletedBy === null || card.deletedBy === input.userId || card.touchedAt === null : card.deletedAt !== null) throw new Error('Card unavailable')
    const removal = action.kind === 'resolved' ? await tx.query`SELECT COALESCE((SELECT 'page@'||p.deletedAt FROM pages p WHERE p.id=g.pageId AND p.deletedAt IS NOT NULL),g.node||'@'||g.deletedAt) AS identity FROM genericCards g WHERE qid=${card.qid}`.executeOneOrThrow(s.object({identity:s.string()})) : null
    const patch = action.kind === 'rated' ? action.patch
      : action.kind === 'reviewed' ? {...action.result, repeatAt:new Date(action.result.repeatAt), reviewCount:card.reviewCount + 1}
      : action.kind === 'frozen' ? {frozenAt:action.frozenAt === null ? null : new Date(action.frozenAt)}
      // Accept records the generic projection's removal identity; a later removal reopens review.
      : action.kind === 'resolved' ? {deletedAt:now,deletedBy:card.deletedBy,node:card.node,acceptedRemoval:removal?.identity ?? null}
      : {}
    await writeOwnCard(tx, card, patch, now)
    let review: ReviewEvent | undefined
    if (action.kind === 'reviewed') {
      if (action.score === 0) await mintMissedClassCell(tx, { userId: input.userId, qid: card.qid, pageId: card.pageId, now, translationLanguage: input.translationLanguage ?? 'en', practiceRep: input.practiceRep ?? 0 })
      review = {eventId:input.eventId ?? ulid(),occurredAt:now.getTime(),score:action.score,durationSeconds:action.durationSeconds,result:action.result,attributes:{importance:card.importance,difficulty:card.difficulty,promptType:card.promptType}}
      await appendReviewEvent(tx,{userId:input.userId,qid:card.qid,deviceId:input.deviceId,stageAt:card.stage,data:review})
    }
    return {review}
  },{exclusive:true}))
}

type LearnerCard = Awaited<ReturnType<typeof readLearnerCard>>

/** Owner actions update only stored card values, independently of block defaults. */
export async function writeOwnCard(tx: WorkspaceDb, card: LearnerCard, patch: Partial<LearnerCard>, now: Date) {
  await tx.query`${tx.flashcards.insert(card)} ON CONFLICT(qid) DO NOTHING`.run()
  await tx.query`${tx.flashcards.update({...patch,updatedAt:now,touchedAt:now})}
    WHERE ${tx.flashcards.qid.eq(card.qid)} AND ${tx.flashcards.userId.eq(card.userId)}`.run()
}

async function mintMissedClassCell(db: WorkspaceDb, input: { userId: string; qid: string; pageId: string; now: Date; translationLanguage: string; practiceRep: number }) {
  const rules = await db.query`SELECT parts.node FROM ${db.pages},
    crdt_attr_lookup('idx_pages_crdt_attr',${input.qid},${db.pages}.rowid) parts
    WHERE ${db.pages.id.eq(input.pageId)} AND ${db.pages.deletedAt.isNull()}
      AND json_extract(parts.fields,'$.type')='rule' AND json_extract(parts.fields,'$.qid')=${input.qid} LIMIT 1`
    .execute(nodeSchema)
  if (rules.length === 0) return
  const row = await db.query`SELECT ${pageSourceColumns(db.pages)} FROM ${db.pages} WHERE ${db.pages.id.eq(input.pageId)} AND ${db.pages.deletedAt.isNull()}`.executeOne(db.pages.__selectSchema)
  if (!row) return
  const content = readPageDocument(crdt.load(row.data).toJSON(), row).content
  if (!('children' in content)) return
  const rule = findBlock(content.children, 'rule', block => 'qid' in block && block.qid === input.qid) as RuleBlock | undefined
  if (!rule) return
  const selected = await listAdoptedRuleMembers(db, {
    workspaceId: '', language: rule.language, inflectionClass: rule.key, rep: input.practiceRep,
  })
  const pair = rulePracticePair(rule, selected.members, selected.rep)
  if (!pair?.form.formRecallQuestionId || !selected.pageId) return
  await requestPredictedCell(db, { ...input, pageId: selected.pageId, cell: pair.cell })
}

/** Explicit request and class misses share the same idempotent authored insertion. */
export function requestPredictedCell(db: WorkspaceDb, input: { userId: string; pageId: string; cell: string; now?: Date; translationLanguage: string }) {
  return db.transaction(tx => insertPredictedCell(tx, input), { exclusive: true })
}

async function insertPredictedCell(db: WorkspaceDb, input: { userId: string; pageId: string; cell: string; now?: Date; translationLanguage: string }) {
  const row = await db.query`SELECT ${pageSourceColumns(db.pages)} FROM ${db.pages} WHERE ${db.pages.id.eq(input.pageId)} AND ${db.pages.deletedAt.isNull()}`.executeOneOrThrow(db.pages.__selectSchema)
  const doc = crdt.load(row.data, { user: input.userId })
  const content = readPageDocument(doc.toJSON(), row).content
  if (content.kind !== 'lexiconEntry' || content.entry.kind !== 'lexeme' || !('children' in content)) throw new Error('Cell source unavailable')
  const entry: Lexeme = content.entry
  const qid = entry.paradigm[input.cell]?.formRecallQuestionId
  if (!qid) throw new Error('Cell identity unavailable')
  if (content.children.some(block => hasQid(block, new Set([qid])))) return
  const since = doc.version()
  doc.edit(edit => { edit.insert('content.children', content.children.length, pageDocumentInput(mintQuestionBlocks(entry, qid, 'formRecallParadigmCell', input.translationLanguage)) as Parameters<typeof edit.insert>[2]) })
  // Card derivation reads the new card's kind from the page's entry (question-card-triggers-ddl.ts).
  await commitPageEdit(db, { pageId: input.pageId, userId: input.userId, now: input.now ?? new Date(), frame: doc.encodeUpdate(since).bytes, tel: nullTel })
}

/** History only. Inserting a review never mutates learner card state. */
export async function appendReviewEvent(db:WorkspaceDb,input:{userId:string;qid:string;deviceId:string;stageAt:number;data:ReviewEvent}) {
  const review = reviewEventSchema.parse(input.data)
  const card=await db.query`SELECT ${db.flashcards.all} FROM ${learnerCards(input.userId)} WHERE ${db.flashcards.qid.eq(input.qid)}`.executeOne(db.flashcards.__selectSchema)
  if (!card) throw new Error('Review card unavailable')
  const existing=await db.query`SELECT ${db.flashcardEvents.all} FROM ${db.flashcardEvents} WHERE ${db.flashcardEvents.id.eq(review.eventId)} AND ${db.flashcardEvents.userId.eq(input.userId)} AND ${db.flashcardEvents.qid.eq(input.qid)}`.executeOne(db.flashcardEvents.__selectSchema)
  if(existing) {
    if(existing.userId!==input.userId || existing.qid!==input.qid || existing.deviceId!==input.deviceId || existing.stageAt!==input.stageAt || existing.occurredAt.getTime()!==review.occurredAt || !same(existing.data,review))throw new Error('Conflicting review event identity')
    return
  }
  await db.query`${db.flashcards.insert(card)} ON CONFLICT(qid) DO NOTHING`.run()
  await db.query`${db.flashcardEvents.insert({id:review.eventId,userId:input.userId,qid:input.qid,deviceId:input.deviceId,stageAt:input.stageAt,occurredAt:new Date(review.occurredAt),data:review})}`.run()
}

export function readLearnerCard(db:WorkspaceDb,userId:string,qid:string) {
  return db.query`SELECT ${cardReadColumns(db.flashcards)} FROM ${learnerCards(userId)} WHERE turso_session_user()=${userId} AND ${db.flashcards.qid.eq(qid)} AND ${cardVisibleTo(db.flashcards, userId)}`.executeOneOrThrow(db.flashcards.__selectSchema)
}

export function acceptCard(db: WorkspaceDb, input: { userId: string; deviceId: string; qid: string; now?: Date }) {
  return actOnCard(db, { ...input, action: { kind: 'resolved' } })
}
export type CloneMedia = {
  destination?(source: string, index: number): string
  copy(input: { source: string; destination: string; sourceSpaceId: string; privateSpaceId: string }): Promise<void>
}
/** Stable destination ids supplied by operation owner. Media completes before atomic commit. */
export async function cloneDeletedCard(db: WorkspaceDb, input: { userId: string; deviceId: string; qid: string; privateSpaceId: string; personalRootId: string; pageId: string; eventId: string; media?: CloneMedia; now?: Date }) {
  const retained = await db.query`SELECT ${cardReadColumns(db.flashcards)} FROM ${db.flashcards} WHERE ${db.flashcards.qid.eq(input.qid)} AND ${db.flashcards.userId.eq(input.userId)}`.executeOne(db.flashcards.__selectSchema)
  if(retained?.pageId === input.pageId && retained.deletedAt === null)return
  const card = await readLearnerCard(db,input.userId,input.qid)
  if (card.deletedBy === null || card.deletedBy === input.userId || card.touchedAt === null) throw new Error('Clone requires available deleted content')
  const page = await db.query`SELECT ${pageSourceColumns(db.pages)} FROM ${db.pages} WHERE ${db.pages.id.eq(card.pageId)}`.executeOne(db.pages.__selectSchema)
  if (!page) throw new Error('Clone requires available deleted content')
  const source = readPageDocument(crdt.load(page.data).toJSON(), page)
  const currentParts = collectQuestionParts(source.content,card.qid)
  const parts = currentParts.length > 0 ? currentParts : await removedQuestionParts(db, card.pageId, card.qid)
  if (parts.length === 0) throw new Error('Clone requires available deleted content')
  const media = new Map<string,string>()
  for (const [index, src] of collectMediaSources(parts).entries()) media.set(src, input.media?.destination?.(src, index) ?? `${input.privateSpaceId}/clone/${encodeURIComponent(input.eventId)}/${encodeURIComponent(src)}`)
  for (const [src,destination] of media) {
    if (!input.media) throw new Error(`Missing media: ${src}`)
    await input.media.copy({ source: src, destination, sourceSpaceId: input.privateSpaceId, privateSpaceId: input.privateSpaceId })
  }
  // A cloze is inline: the page root admits only block-level blocks (block-schema CONTAINMENT, enforced by crdt_rs
  // rules), so the copy wraps it in a paragraph, its inline host.
  const content = (rewrite(parts, item => ({ ...item, ...(typeof item.src === 'string' && media.has(item.src) ? { src: media.get(item.src) } : {}) })) as Blocks)
    .map((part): Blocks[number] => part.type === 'cloze' ? { id: blockId(), type: 'p', children: [part] } : part)
  return db.transaction(async tx => {
    const exists = await tx.query`SELECT ${tx.pages.id} FROM ${tx.pages} WHERE ${tx.pages.id.eq(input.pageId)}`.executeOne({ id: tx.pages.id })
    if (exists) throw new Error('Clone destination identity conflict')
    const doc = newPageDocument({ title: source.title, deletedAt: null, content: { kind: 'note', children: content } }, input.userId)
    await tx.query`${tx.pages.insert({ id: input.pageId, data: doc.encodeSnapshot().bytes, updatedAt: input.now ?? new Date(), title: source.title, titleKey: searchKey(source.title), kind: 'note', editedBy: input.userId })}`.run()
    await tx.query`${tx.pagePermissions.insert({ pageId: input.pageId, principalKind: 'user', principalId: input.userId, level: 3 })}`.run()
    await tx.query`${tx.pageRelations.insert({ fromPageId: input.personalRootId, toPageId: input.pageId, type: 'child' })}`.run()
    // Clone is an explicit learner action; retain the same card and its schedule.
    await tx.query`${tx.flashcards.update({ pageId: input.pageId, deletedAt: null, deletedBy: null })}
      WHERE ${tx.flashcards.userId.eq(input.userId)} AND ${tx.flashcards.qid.eq(input.qid)}`.run()
    // The clone is a page creation by the learner: the write path's lifecycle derivation locates the card on the new
    // part (node, sort key) and derives the copied parts' links (page-relations-derivation.ts), as commitPageEdit does.
    for (const statement of pageLifecycleStatements(null, { pageId: input.pageId, userId: input.userId, updatedAt: (input.now ?? new Date()).getTime() })) await tx.query`${statement}`.run()
  })
}

export async function readLearnerActivity(db: WorkspaceDb, userId: string) {
  // The caller's own activity (progress is private, flashcards-spaces.spec.md spaces-teacher-read-only): stored rows, plus
  // untouched questions on pages where the caller holds a grant. There an untouched card is Not studied; every other card
  // (stored rows elsewhere, touched cards) is the latest card state.
  const granted=new Set((await db.query`SELECT p.id AS pageId FROM pages p WHERE p.id IN ${readablePagesOf(userId)}`.execute(s.object({pageId:s.string()}))).map(row=>row.pageId))
  const notStudied=(card:{touchedAt:Date|null;pageId:string})=>card.touchedAt===null && granted.has(card.pageId)
  const cards=await db.query`SELECT ${cardReadColumns(db.flashcards)} FROM ${learnerCards(userId)} WHERE ${db.flashcards.userId.eq(userId)} ORDER BY ${db.flashcards.qid}`.execute(db.flashcards.__selectSchema)
    const rows=await db.query`SELECT ${db.flashcardEvents.all} FROM ${db.flashcardEvents} WHERE ${db.flashcardEvents.userId.eq(userId)} ORDER BY ${db.flashcardEvents.occurredAt}, ${db.flashcardEvents.id}`.execute(db.flashcardEvents.__selectSchema)
  const reviews=rows.map(row=>Object.assign({},row.data,{qid:row.qid})).toSorted((a,b)=>a.occurredAt-b.occurredAt || compareText(a.eventId,b.eventId))
  return {userId,reviewedQuestionCount:new Set(reviews.map(review=>review.qid)).size,reviewCount:reviews.length,
    reviews:reviews.map(review=>({eventId:review.eventId,qid:review.qid,score:review.score,durationSeconds:review.durationSeconds,occurredAt:review.occurredAt})),
    notStudied:cards.filter(card=>notStudied(card)).map(card=>card.qid),
    latestByQid:cards.filter(card=>!notStudied(card)).map(card=>({qid:card.qid,pageId:card.pageId,attributes:{importance:card.importance,difficulty:card.difficulty,promptType:card.promptType},schedule:{stage:card.stage,repeatAt:card.repeatAt==='now'?'now':card.repeatAt.getTime(),ttaSeconds:card.ttaSeconds,graduated:card.graduated},frozenAt:card.frozenAt?.getTime()??null}))}
}

function canonical(value: unknown): unknown {
  if (Array.isArray(value)) return value.map(child => canonical(child))
  if (value && typeof value === 'object') return Object.fromEntries(Object.entries(value).toSorted(([a],[b]) => a.localeCompare(b)).map(([key,item]) => [key,canonical(item)]))
  return value
}
function same(a: unknown,b: unknown) { return JSON.stringify(canonical(a)) === JSON.stringify(canonical(b)) }
function rewrite(value: unknown,fn: (item: Record<string,unknown>) => Record<string,unknown>): unknown {
  if (Array.isArray(value)) return value.map(child => rewrite(child,fn))
  if (value && typeof value === 'object') return fn(Object.fromEntries(Object.entries(value).map(([key,child]) => [key,rewrite(child,fn)])))
  return value
}


export function readLearnerCards(db:WorkspaceDb,userId:string) {
  return db.query`SELECT ${cardReadColumns(db.flashcards)} FROM ${learnerCards(userId)} WHERE ${cardVisibleTo(db.flashcards, userId)} ORDER BY ${db.flashcards.qid}`.execute(db.flashcards.__selectSchema)
}


function compareText(a:string,b:string) { return a<b?-1:a>b?1:0 }

/** Content reads respect the currently permitted pages; absence exposes no retained content. */
export async function readCardSource(db:WorkspaceDb,userId:string,qid:string) {
  const card=await db.query`SELECT ${cardReadColumns(db.flashcards)} FROM ${learnerCards(userId)}
    WHERE turso_session_user()=${userId} AND ${db.flashcards.qid.eq(qid)} AND ${cardVisibleTo(db.flashcards, userId)}`.executeOne(db.flashcards.__selectSchema)
  if(!card)return null
  const row=await db.query`SELECT ${pageSourceColumns(db.pages)} FROM ${db.pages} WHERE ${db.pages.id.eq(card.pageId)}`.executeOne(db.pages.__selectSchema)
  if(!row)return null
  const document=readPageDocument(crdt.load(row.data).toJSON(), row)
  const currentParts = collectQuestionParts(document.content,card.qid)
  const parts = currentParts.length > 0 ? currentParts : await removedQuestionParts(db, card.pageId, card.qid)
  return {card,parts,deletion:card.deletedBy?{opId:String(row.updatedAt.getTime()),actorUserId:card.deletedBy}:null}
}


/** Restore retained content owned by this learner; deletion metadata is plain row state. */
export function restoreQuestionDeletion(db:WorkspaceDb,input:{userId:string;pageId:string;qid:string;opId:string;now?:Date}) {
  return db.transaction(async tx=>{
    const row=await tx.query`SELECT ${pageSourceColumns(tx.pages)} FROM ${tx.pages} WHERE ${tx.pages.id.eq(input.pageId)}`.executeOneOrThrow(tx.pages.__selectSchema)
    const source=readPageDocument(crdt.load(row.data).toJSON(), row)
    const retained=await removedQuestionParts(tx,input.pageId,input.qid)
    if(retained.length === 0)return
    const doc=crdt.load(row.data,{user:input.userId})
    if(!('children' in source.content))throw new Error('Question source cannot be restored')
    const children = source.content.children
    const existing=collectQuestionParts(source.content,input.qid) as Record<string,unknown>[]
    // A removed cloze is the inline node alone: it goes back inside a paragraph, as Clone does.
    const restored=(retained as Record<string,unknown>[]).filter(part=>!existing.some(p=>p.id===part.id))
      .map(part=>part.type==='cloze'?{id:blockId(),type:'p',children:[part]}:part)
    doc.edit(edit=>{edit.insert('content.children',children.length,restored as Parameters<typeof edit.insert>[2])})
    // Restored parts re-derive their cards in this commit (option A: the write path derives, no page trigger).
    await commitPageEdit(tx,{pageId:input.pageId,userId:input.userId,frame:doc.encodeSnapshot().bytes,now:input.now??new Date(),tel:nullTel})
  })
}

export function restorePageDeletion(db:WorkspaceDb,input:{userId:string;pageId:string;opId:string;now?:Date}) {
  // A fields-only commit: the lifecycle statements un-delete the page's cards and links (option A).
  return commitPageEdit(db,{pageId:input.pageId,userId:input.userId,fields:{deletedAt:null},frame:new Uint8Array(),now:input.now??new Date(),tel:nullTel})
}

/** All part controls write the canonical first part's tuple. No learner state is authored. */
export function writeQuestionDefaults(db:WorkspaceDb,input:{userId:string;pageId:string;qid:string;patch:{importance?:'low'|'medium'|'high';difficulty?:'low'|'medium'|'high'};now:Date;tel:Tel}) {
  return Promise.resolve(db.transaction(async tx=>{
    const row=await tx.query`SELECT ${pageSourceColumns(tx.pages)} FROM ${tx.pages} WHERE ${tx.pages.id.eq(input.pageId)}`.executeOneOrThrow(tx.pages.__selectSchema)
    const doc=crdt.load(row.data,{user:input.userId})
    const source=readPageDocument(doc.toJSON(), row)
    const path=questionPath(source.content,'content',input.qid)
    if(!path)throw new Error('Question source unavailable')
    doc.edit(edit=>{for(const [field,value] of Object.entries(input.patch))edit.set(path,field,s.enum(['low','medium','high']).parse(value))})
    await commitPageEdit(tx,{pageId:row.id,userId:input.userId,frame:doc.encodeSnapshot().bytes,now:input.now,tel:input.tel})
  }))
}
function questionPath(value:unknown,path:string,qid:string):string|null {
  if(Array.isArray(value)) {
    for(const child of value as unknown[]){
      if(!child || typeof child!=='object' || !('id' in child) || typeof child.id!=='string')continue
      const found=questionPath(child,`${path}.${child.id}`,qid)
      if(found)return found
    }
  }else if(value && typeof value==='object') {
    if('qid' in value && value.qid===qid)return path
    for(const [key,child] of Object.entries(value)){const found=questionPath(child,`${path}.${key}`,qid);if(found)return found}
  }
  return null
}
